import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import "moment/locale/fr";
import { useParams, useNavigate } from "react-router-dom";

function InlineEdit({ value, onSave, disabled, type = "text", className = "" }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value ?? "");

  const handleSave = () => {
    setEditing(false);
    const newVal = type === "number" ? parseFloat(text) || 0 : text.trim();
    if (String(newVal) !== String(value ?? "")) {
      onSave(newVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") { setText(value ?? ""); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] ${className}`}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-text hover:bg-gray-100 rounded px-1 py-0.5 -mx-1 transition-colors"
      title="Cliquer pour modifier"
    >
      {value != null && value !== "" ? value : <span className="text-gray-400 italic">—</span>}
    </span>
  );
}

moment.locale("fr");

const APPOINTMENT_STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  inProgress: "En cours",
  waitPayment: "En attente paiement",
  paymentConfirmed: "Paiement confirmé",
  refused: "Refusé",
  cancelled: "Annulé",
};

const APPOINTMENT_STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  inProgress: "bg-indigo-100 text-indigo-800",
  waitPayment: "bg-blue-100 text-blue-800",
  paymentConfirmed: "bg-purple-100 text-purple-800",
  refused: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

const OFFER_STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  refused: "Refusée",
};

const STATUS_OPTIONS = Object.keys(APPOINTMENT_STATUS_LABELS);

export default function AppointmentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [client, setClient] = useState(null);
  const [couturier, setCouturier] = useState(null);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  useEffect(() => {
    async function fetchDetails() {
      setLoading(true);
      const appointSnap = await getDoc(doc(db, "appointments", id));
      if (!appointSnap.exists()) {
        setLoading(false);
        return;
      }
      const appointData = appointSnap.data();
      setAppointment(appointData);
      setNewStatus(appointData.status);

      if (appointData.clientId) {
        const clientSnap = await getDoc(doc(db, "users", appointData.clientId));
        if (clientSnap.exists()) setClient({ id: appointData.clientId, ...clientSnap.data() });
      }
      if (appointData.couturierId) {
        const couturierSnap = await getDoc(doc(db, "users", appointData.couturierId));
        if (couturierSnap.exists()) setCouturier({ id: appointData.couturierId, ...couturierSnap.data() });
      }
      if (appointData.offerId) {
        const offerSnap = await getDoc(doc(db, "offers", appointData.offerId));
        if (offerSnap.exists()) setOffer(offerSnap.data());
      }
      setLoading(false);
    }
    fetchDetails();
  }, [id]);

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const handleStatusChange = async (e) => {
    const val = e.target.value;
    setNewStatus(val);
    setSavingStatus(true);
    try {
      await updateDoc(doc(db, "appointments", id), { status: val });
      setAppointment((prev) => ({ ...prev, status: val }));
      showNotif("Statut mis à jour !");
    } catch {
      alert("Erreur lors de la mise à jour du statut");
      setNewStatus(appointment.status);
    }
    setSavingStatus(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Confirmez-vous la suppression de ce rendez-vous ?")) return;
    setDeleting(true);
    try {
      if (appointment.offerId) {
        await updateDoc(doc(db, "offers", appointment.offerId), { status: "pending" });
      }
      await deleteDoc(doc(db, "appointments", id));
      showNotif("Rendez-vous supprimé");
      navigate("/rdv");
    } catch {
      alert("Erreur lors de la suppression");
    }
    setDeleting(false);
  };

  if (!appointment)
    return (
      <PageTemplate
        loading={loading}
        content={
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">Rendez-vous introuvable</p>
            <button
              onClick={() => navigate("/rdv")}
              className="mt-2 text-xs font-medium text-[#801111] hover:text-[#5a0c0c]"
            >
              Retour à la liste
            </button>
          </div>
        }
      />
    );

  const UserCard = ({ label, user, userId }) => (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-3 py-2 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-900">{label}</span>
      </div>
      <div className="p-3">
        {user ? (
          <div className="flex items-center gap-3">
            {user.photo ? (
              <img src={user.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm">
                {user.firstname?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-0.5">
              <p className="text-xs font-medium text-gray-900 truncate">
                {user.firstname} {user.lastname}
              </p>
              <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
              {user.phoneNumber && (
                <p className="text-[10px] text-gray-500">{user.phoneNumber}</p>
              )}
            </div>
            {userId && (
              <button
                onClick={() => navigate(`/users/${userId}`)}
                className="text-xs font-medium text-[#801111] hover:text-[#5a0c0c] whitespace-nowrap"
              >
                Voir
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic text-center py-2">Utilisateur inconnu</p>
        )}
      </div>
    </div>
  );

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          <button
            onClick={() => navigate("/rdv")}
            className="text-xs font-medium text-[#801111] hover:text-[#5a0c0c]"
          >
            ← Retour aux rendez-vous
          </button>

          {/* Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${APPOINTMENT_STATUS_COLORS[appointment.status] || "bg-gray-100 text-gray-800"}`}>
                  {APPOINTMENT_STATUS_LABELS[appointment.status] || appointment.status}
                </span>
                <span className="text-xs text-gray-500">
                  Créé le {appointment.createdAt?.toDate ? moment(appointment.createdAt.toDate()).format("DD/MM/YYYY HH:mm") : "-"}
                </span>
              </div>
              <span className="text-[10px] text-gray-400 font-mono">{id}</span>
            </div>
          </div>

          {/* Client + Couturier */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UserCard label="Client" user={client} userId={client?.id} />
            <UserCard label="Couturier" user={couturier} userId={couturier?.id} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Détails */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-gray-900">Détails du rendez-vous</span>
              </div>
              <div className="p-3 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Statut</span>
                  <select
                    value={newStatus}
                    onChange={handleStatusChange}
                    disabled={savingStatus || deleting}
                    className="border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:opacity-50"
                  >
                    {STATUS_OPTIONS.map((key) => (
                      <option key={key} value={key}>
                        {APPOINTMENT_STATUS_LABELS[key]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Date RDV</span>
                  <span className="text-gray-900">
                    <InlineEdit
                      value={appointment.date || ""}
                      type="date"
                      onSave={async (val) => {
                        try {
                          await updateDoc(doc(db, "appointments", id), { date: val });
                          setAppointment((prev) => ({ ...prev, date: val }));
                          showNotif("Date mise à jour !");
                        } catch { alert("Erreur lors de la mise à jour"); }
                      }}
                    />
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Heure</span>
                  <span className="text-gray-900">
                    <InlineEdit
                      value={appointment.time || ""}
                      type="time"
                      onSave={async (val) => {
                        try {
                          await updateDoc(doc(db, "appointments", id), { time: val });
                          setAppointment((prev) => ({ ...prev, time: val }));
                          showNotif("Heure mise à jour !");
                        } catch { alert("Erreur lors de la mise à jour"); }
                      }}
                    />
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date création</span>
                  <span className="text-gray-900">
                    {appointment.createdAt?.toDate ? moment(appointment.createdAt.toDate()).format("DD/MM/YYYY HH:mm") : "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date paiement</span>
                  <span className="text-gray-900">
                    {appointment.paidAt?.toDate ? moment(appointment.paidAt.toDate()).format("DD/MM/YYYY HH:mm") : "-"}
                  </span>
                </div>
              </div>

              {appointment.status === "cancelled" && (
                <div className="mx-3 mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
                  <p className="font-medium text-red-800">Raison d'annulation</p>
                  <p className="text-red-700 mt-0.5">{appointment.cancelReason || "Non spécifiée"}</p>
                  {appointment.cancelledBy && (
                    <p className="text-red-500 mt-0.5 text-[10px]">Par : {appointment.cancelledBy}</p>
                  )}
                </div>
              )}
            </div>

            {/* Offre */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-gray-900">Offre du couturier</span>
              </div>
              <div className="p-3">
                {offer ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        { pending: "bg-yellow-100 text-yellow-800", confirmed: "bg-green-100 text-green-800", refused: "bg-red-100 text-red-800" }[offer.status] || "bg-gray-100 text-gray-800"
                      }`}>
                        {OFFER_STATUS_LABELS[offer.status] || offer.status}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        <InlineEdit
                          value={offer.price}
                          type="number"
                          onSave={async (val) => {
                            if (!appointment.offerId) return;
                            try {
                              await updateDoc(doc(db, "offers", appointment.offerId), { price: val });
                              setOffer((prev) => ({ ...prev, price: val }));
                              showNotif("Prix mis à jour !");
                            } catch { alert("Erreur lors de la mise à jour"); }
                          }}
                        /> €
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-700 bg-gray-50 rounded p-2">
                        <InlineEdit
                          value={offer.message}
                          onSave={async (val) => {
                            if (!appointment.offerId) return;
                            try {
                              await updateDoc(doc(db, "offers", appointment.offerId), { message: val });
                              setOffer((prev) => ({ ...prev, message: val }));
                              showNotif("Message mis à jour !");
                            } catch { alert("Erreur lors de la mise à jour"); }
                          }}
                        />
                      </p>
                    </div>
                    {offer.photos && offer.photos.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {offer.photos.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt="" className="w-full h-20 object-cover rounded border border-gray-200 hover:border-[#801111] transition-colors" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic text-center py-4">Offre non trouvée</p>
                )}
              </div>
            </div>
          </div>

          {/* Danger zone */}
          <div className="flex justify-end">
            <button
              onClick={handleDelete}
              disabled={savingStatus || deleting}
              className="px-4 py-1.5 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? "Suppression..." : "Supprimer le rendez-vous"}
            </button>
          </div>
        </div>
      }
    />
  );
}
